import React from 'react';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import clsx from 'clsx';

import { Checkbox } from '@oysterjs/ui/checkbox';
import { Spinner } from '@oysterjs/ui/spinner';

import type { KnowledgeGraphEntityType } from '~/api/graphql';
import { entityTypeBadgeLabel, entityTypeIcon } from './entity-display';

/**
 * Keep the dialog panel at its previous height while content changes, then
 * transition to the newly measured height. CSS cannot animate `height: auto`,
 * so measuring the inner content avoids exposing the dark backdrop during a
 * document-search result change.
 */
export function SmoothAutoHeight({
  children,
  className,
  style,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [height, setHeight] = React.useState<number>();

  React.useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content || typeof ResizeObserver === 'undefined') return;

    const updateHeight = () => setHeight(content.getBoundingClientRect().height);
    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      {...props}
      className={clsx(
        'overflow-hidden bg-white transition-[height] duration-200 ease-out motion-reduce:transition-none dark:bg-neutral-900',
        className
      )}
      style={{ ...style, height }}
    >
      <div ref={contentRef} className="flow-root">
        {children}
      </div>
    </div>
  );
}

/**
 * The bordered, height-animated area under the context bar. With a tab bar
 * above it, it is the tab panel the active tab labels.
 */
export function PickerViewport(props: {
  children: React.ReactNode;
  panelId?: string;
  labelledBy?: string;
}) {
  return (
    <SmoothAutoHeight
      data-testid="ams-picker-viewport"
      id={props.panelId}
      role={props.panelId ? 'tabpanel' : undefined}
      aria-labelledby={props.labelledBy}
      className="overflow-hidden rounded-b-lg border-x border-b border-neutral-200 dark:border-neutral-700"
    >
      {props.children}
    </SmoothAutoHeight>
  );
}

/** The search box across the top of the picker, with an optional segment on its right. */
export function SearchRail(props: {
  query: string;
  onQueryChange: (query: string) => void;
  placeholder: string;
  ariaLabel: string;
  trailing?: React.ReactNode;
}) {
  const { query, onQueryChange, placeholder, ariaLabel, trailing } = props;
  return (
    <div
      className={clsx(
        'relative flex w-full overflow-hidden rounded-lg',
        'border border-neutral-950/10 bg-white shadow-sm',
        'focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/20',
        'dark:border-white/10 dark:bg-white/5'
      )}
    >
      <div className="relative min-w-0 flex-1">
        <MagnifyingGlassIcon
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-500 dark:text-neutral-400"
          aria-hidden="true"
        />
        <input
          type="text"
          autoFocus
          value={query}
          onChange={(e) => onQueryChange(e.currentTarget.value)}
          placeholder={placeholder}
          aria-label={ariaLabel}
          className={clsx(
            'w-full border-0 bg-transparent py-2.5 pl-9 pr-3 sm:py-2',
            'text-base/6 text-neutral-950 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 sm:text-sm/6',
            'focus:outline-none focus:ring-0 dark:text-white'
          )}
        />
      </div>
      {trailing}
    </div>
  );
}

/** A dropdown that sits in the right segment of {@link SearchRail}. */
export function RailListbox<T extends string>(props: {
  value: T;
  onChange: (value: T) => void;
  options: ReadonlyArray<{ value: T; label: string; disabled?: boolean }>;
  ariaLabel: string;
}) {
  const { value, onChange, options, ariaLabel } = props;
  const label = options.find((o) => o.value === value)?.label ?? options[0]?.label;
  return (
    <Listbox value={value} onChange={onChange}>
      <div className="relative shrink-0">
        <ListboxButton
          aria-label={ariaLabel}
          className={clsx(
            'flex h-full min-w-[6.5rem] items-center justify-center gap-1',
            'border-l border-neutral-950/10 bg-neutral-50 px-3 text-sm font-medium text-neutral-700',
            'hover:bg-neutral-100 focus:outline-none',
            'dark:border-white/10 dark:bg-white/5 dark:text-neutral-200 dark:hover:bg-white/10'
          )}
        >
          {label}
          <ChevronDownIcon
            className="size-3.5 text-neutral-400 dark:text-neutral-500"
            aria-hidden="true"
          />
        </ListboxButton>
        <ListboxOptions
          anchor="bottom end"
          className={clsx(
            'z-20 mt-1 min-w-36 rounded-lg bg-white p-1 shadow-lg',
            'ring-1 ring-black/5 focus:outline-none',
            'dark:bg-neutral-800 dark:ring-white/10'
          )}
        >
          {options.map((option) => (
            <ListboxOption
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              className={clsx(
                'cursor-pointer rounded-md px-3 py-1.5 text-sm text-neutral-700',
                'data-[focus]:bg-neutral-100 data-[selected]:font-medium data-[selected]:text-sky-700 dark:data-[selected]:text-sky-300',
                'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40',
                'dark:text-neutral-200 dark:data-[focus]:bg-white/10'
              )}
            >
              {option.label}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  );
}

/**
 * The strip above the result viewport: where the user is on the left and,
 * when given, what they have selected on the right.
 */
export function PickerContextBar(props: { children: React.ReactNode; summary?: string }) {
  return (
    <div
      data-testid="ams-picker-context-bar"
      className="relative z-10 mt-3 flex items-center justify-between gap-3 rounded-t-lg border border-neutral-200 dark:border-neutral-700 px-3 py-1.5 text-xs text-neutral-500 dark:text-neutral-400"
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">{props.children}</div>
      {props.summary !== undefined && (
        <div className="shrink-0 font-medium text-sky-700 dark:text-sky-300">{props.summary}</div>
      )}
    </div>
  );
}

/** One tab in the context bar, which ContextTabs holds. */
export function ContextTab(props: {
  label: string;
  /** Omitted while the count is unknown, as during the first read. */
  count?: string;
  active: boolean;
  /** The tab's own id, which its panel is labelled by. */
  id: string;
  /** The id of the panel the tab shows (PickerViewport's panelId). */
  controls: string;
  onClick?: () => void;
}) {
  const { label, count, active, id, controls, onClick } = props;
  return (
    <button
      type="button"
      id={id}
      role="tab"
      aria-selected={active}
      aria-controls={controls}
      onClick={onClick}
      className={clsx(
        'flex shrink-0 items-center gap-1.5 border-b-2 py-1.5 text-xs',
        active
          ? 'border-primary-500 font-medium text-primary-600 dark:border-primary-400 dark:text-primary-400'
          : 'border-transparent text-neutral-500 hover:text-neutral-800 dark:text-neutral-400 dark:hover:text-neutral-200'
      )}
    >
      {label}
      {count !== undefined && (
        <span
          className={clsx(
            'min-w-5 rounded-full px-1.5 text-center text-[11px] font-medium',
            active
              ? 'bg-primary-100 text-primary-700 dark:bg-primary-500/15 dark:text-primary-300'
              : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300'
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}

/**
 * The context bar's tabs. The list spans the bar's full height, so each tab's
 * underline sits on the bar's bottom border, and scrolls sideways rather than
 * overlapping the crumb when the bar is too narrow for every tab.
 */
export function ContextTabs(props: { children: React.ReactNode; label: string }) {
  return (
    <div
      role="tablist"
      aria-label={props.label}
      className={clsx(
        '-mt-1.5 -mb-[7px] ml-2 flex min-w-0 items-stretch gap-4 overflow-x-auto',
        '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
      )}
    >
      {props.children}
    </div>
  );
}

export function ContextBackButton(props: { onClick: () => void; ariaLabel: string }) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      aria-label={props.ariaLabel}
      className="shrink-0 text-neutral-500 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200"
    >
      <ChevronLeftIcon className="size-3.5" aria-hidden="true" />
    </button>
  );
}

/**
 * A place in the context bar. A long name is cut short, so the tabs beside it
 * keep their room, and shown whole on hover.
 */
export function ContextCrumb(props: { children: React.ReactNode; onClick?: () => void }) {
  const parts = React.Children.toArray(props.children);
  const title = parts.every((p) => typeof p === 'string' || typeof p === 'number')
    ? parts.join('')
    : undefined;
  const className = 'min-w-0 max-w-56 truncate font-medium text-neutral-700 dark:text-neutral-300';
  if (!props.onClick) {
    return (
      <span className={className} title={title}>
        {props.children}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={props.onClick}
      title={title}
      className={clsx(className, 'hover:text-neutral-900 dark:hover:text-white')}
    >
      {props.children}
    </button>
  );
}

export function ContextCrumbSeparator() {
  return (
    <ChevronRightIcon
      className="size-3.5 shrink-0 text-neutral-400 dark:text-neutral-500"
      aria-hidden="true"
    />
  );
}

/**
 * Discovery row with a pin checkbox and a separate navigate action. Without
 * `onNavigate` the row is pin-only; `selectDisabled` blocks the pin alone.
 */
export function PinSearchResultRow(props: {
  icon: React.ReactNode;
  title: string;
  secondary?: string;
  typeBadge?: string;
  badges?: string[];
  disabled?: boolean;
  selectDisabled?: boolean;
  /** Omit to leave the checkbox out, for a list where the row itself is the
   * answer and nothing acts on a selection. */
  selected?: boolean;
  onToggle?: () => void;
  onNavigate?: () => void;
  selectLabel?: string;
  navigateLabel: string;
}) {
  const {
    icon,
    title,
    secondary,
    typeBadge,
    badges,
    disabled,
    selectDisabled,
    selected,
    onToggle,
    onNavigate,
    selectLabel,
    navigateLabel
  } = props;
  const content = (
    <>
      {icon}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-neutral-900 dark:text-white">{title}</div>
        {secondary && (
          <div className="truncate text-xs text-neutral-500 dark:text-neutral-400">{secondary}</div>
        )}
      </div>
      {(typeBadge || (badges && badges.length > 0)) && (
        <div className="flex shrink-0 items-center gap-1">
          {typeBadge && (
            <span className="rounded bg-sky-50 dark:bg-sky-900 px-1.5 py-0.5 text-[11px] font-medium text-sky-700 dark:text-sky-300">
              {typeBadge}
            </span>
          )}
          {badges?.map((badge) => (
            <span
              key={badge}
              className="rounded bg-neutral-100 dark:bg-neutral-700 px-1.5 py-0.5 text-[11px] font-medium text-neutral-600 dark:text-neutral-400"
            >
              {badge}
            </span>
          ))}
        </div>
      )}
    </>
  );
  return (
    <li className="flex items-center gap-2 px-3 py-2.5">
      {onToggle && (
        <Checkbox
          color="sky"
          checked={!!selected}
          disabled={disabled || selectDisabled}
          onChange={onToggle}
          aria-label={selectLabel}
        />
      )}
      {onNavigate ? (
        <button
          type="button"
          disabled={disabled}
          onClick={onNavigate}
          aria-label={navigateLabel}
          className="flex min-w-0 flex-1 items-center gap-3 text-left disabled:cursor-not-allowed disabled:opacity-60"
        >
          {content}
          <ChevronRightIcon
            className="size-4 shrink-0 text-neutral-400 dark:text-neutral-500"
            aria-hidden="true"
          />
        </button>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-3">{content}</div>
      )}
    </li>
  );
}

export function DocumentSectionHeader(props: { children: React.ReactNode }) {
  return (
    <div className="border-y border-neutral-100 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 px-3 py-1.5 text-xs font-semibold text-neutral-600 dark:text-neutral-400 first:border-t-0">
      {props.children}
    </div>
  );
}

export function DocumentListFrame(props: {
  children: React.ReactNode;
  loadedCount?: number;
  totalCount?: number;
  /** null leaves the count bar out; omitted, it counts documents. */
  countLabel?: string | null;
}) {
  const { children, loadedCount, totalCount, countLabel } = props;
  const countText =
    countLabel === undefined
      ? `${loadedCount ?? 0} of ${totalCount ?? 0} document${totalCount === 1 ? '' : 's'}`
      : countLabel;
  return (
    <div>
      <div className="max-h-[45vh] overflow-y-auto">{children}</div>
      {countText !== null && (
        <div
          data-testid="ams-document-count-bar"
          className="border-t border-neutral-100 dark:border-neutral-700 px-3 py-1.5 text-center text-xs text-neutral-500 dark:text-neutral-400"
        >
          {countText}
        </div>
      )}
    </div>
  );
}

/**
 * Single-line pick row. With `onNavigate` everything after the checkbox opens
 * the record, keeping the same icon and spacing as a pick-only row.
 */
export function ContextEntityRow(props: {
  entityType: KnowledgeGraphEntityType;
  label: string;
  secondary?: string;
  badges?: string[];
  folderName?: string;
  /** Omit to hide the trailing modified-date column (e.g. policy rows, which don't track it). */
  modifiedAt?: Date | string;
  /** Hides the sky entity-type pill for rows where it's redundant (e.g. inside a "Policies" section). */
  hideTypeBadge?: boolean;
  disabled?: boolean;
  /** Shown under the label of a disabled row, saying why it can't be picked. */
  disabledReason?: string;
  /** Blocks the pin but leaves the row openable. */
  selectDisabled?: boolean;
  selected: boolean;
  onToggle: () => void;
  ariaLabel: string;
  onNavigate?: () => void;
  navigateLabel?: string;
}) {
  const {
    entityType,
    label,
    secondary,
    badges,
    folderName,
    modifiedAt,
    hideTypeBadge,
    disabled,
    disabledReason,
    selectDisabled,
    selected,
    onToggle,
    ariaLabel,
    onNavigate,
    navigateLabel
  } = props;
  const typeBadge = hideTypeBadge ? undefined : entityTypeBadgeLabel(entityType);
  const reason = disabled ? disabledReason : undefined;
  const content = (
    <>
      {entityTypeIcon(entityType)}
      <div
        className={clsx(
          'min-w-0 flex-1 truncate text-sm text-neutral-800 dark:text-neutral-200',
          disabled && 'opacity-60'
        )}
      >
        {label}
        {reason && (
          <div className="truncate text-xs text-neutral-500 dark:text-neutral-400">{reason}</div>
        )}
      </div>
      {typeBadge && (
        <span className="shrink-0 rounded bg-sky-50 dark:bg-sky-900 px-1.5 py-0.5 text-[11px] font-medium text-sky-700 dark:text-sky-300">
          {typeBadge}
        </span>
      )}
      {secondary && (
        <span className="shrink-0 rounded bg-neutral-100 dark:bg-neutral-700 px-1.5 py-0.5 text-[11px] font-medium text-neutral-600 dark:text-neutral-400">
          {secondary}
        </span>
      )}
      {badges?.map((badge) => (
        <span
          key={badge}
          className="shrink-0 rounded bg-neutral-100 dark:bg-neutral-700 px-1.5 py-0.5 text-[11px] font-medium text-neutral-600 dark:text-neutral-400"
        >
          {badge}
        </span>
      ))}
      {folderName && (
        <span className="shrink-0 text-xs text-neutral-400 dark:text-neutral-500">
          {folderName}
        </span>
      )}
      {modifiedAt && (
        <div className="shrink-0 text-xs text-neutral-500 dark:text-neutral-400">
          {formatModifiedAt(modifiedAt)}
        </div>
      )}
    </>
  );
  return (
    <li className="flex items-center gap-2 px-3 py-2" title={reason}>
      <Checkbox
        color="sky"
        checked={selected}
        disabled={disabled || selectDisabled}
        onChange={onToggle}
        aria-label={ariaLabel}
      />
      {onNavigate ? (
        <button
          type="button"
          onClick={onNavigate}
          aria-label={navigateLabel}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          {content}
          <ChevronRightIcon
            className="size-4 shrink-0 text-neutral-400 dark:text-neutral-500"
            aria-hidden="true"
          />
        </button>
      ) : (
        content
      )}
    </li>
  );
}

/** A link-styled button under a paged list; pages load only when asked. */
export function LoadMoreButton(props: { onClick: () => void; loading: boolean; failed: boolean }) {
  return (
    <div className="flex items-center justify-center gap-2 py-2">
      {props.failed && (
        <span className="text-xs text-red-600 dark:text-red-400">Couldn't load more.</span>
      )}
      <button
        type="button"
        onClick={props.onClick}
        disabled={props.loading}
        className="text-xs font-medium text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 disabled:cursor-default disabled:opacity-60"
      >
        {props.loading ? 'Loading…' : 'Load more'}
      </button>
    </div>
  );
}

export function ListSpinner(props: { label?: string }) {
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <Spinner color="neutral" />
      <div className="text-sm text-neutral-500 dark:text-neutral-400">
        {props.label ?? 'Loading…'}
      </div>
    </div>
  );
}

export function LoadError(props: { children?: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-300">
      <ExclamationTriangleIcon className="size-5 shrink-0" aria-hidden="true" />
      <div>
        {props.children ??
          "Couldn't load documents from your AMS. Try again or attach files manually."}
      </div>
    </div>
  );
}

export function EmptyState(props: { title: string; description: string; icon: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 py-10 text-center">
      {props.icon}
      <h3 className="mt-2 mb-0 text-sm font-semibold font-body text-neutral-900 dark:text-white">
        {props.title}
      </h3>
      <p className="text-sm max-w-md text-neutral-500 dark:text-neutral-400">{props.description}</p>
    </div>
  );
}

export const emptyStateIconClass = 'size-10 text-neutral-400 dark:text-neutral-500';

/** Status and expiration badges shown alongside a policy row's entity-type badge. */
export function policyBadges(
  meta: { status?: string | null; expiresAt?: Date | string | null } | null | undefined
): string[] {
  return [meta?.status ?? undefined, formatExpiresAt(meta?.expiresAt)].filter(
    (value): value is string => Boolean(value)
  );
}

function formatExpiresAt(expiresAt: Date | string | null | undefined): string | undefined {
  if (!expiresAt) return undefined;
  const formatted = formatModifiedAt(expiresAt);
  return formatted ? `Exp ${formatted}` : undefined;
}

/** A date-only YYYY-MM-DD string reads as local midnight, not UTC, so it keeps its day. */
export function formatModifiedAt(modifiedAt: Date | string): string {
  const d =
    typeof modifiedAt === 'string'
      ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(modifiedAt) ? `${modifiedAt}T00:00:00` : modifiedAt)
      : modifiedAt;
  if (!d || isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatRelativeTime(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 30) return 'just now';
  if (diffSec < 60) return `${diffSec} seconds ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
}
